import Phrase from '../../libs/translationsUtils/phrase'
import { Component } from 'preact'
import TopicFavorite from '../topic-list-view/TopicFavorite'
import { publish, ModulesContext } from '../../utils/globals'
import { CurrentUserContext } from '../../server-middleware/userContext'
import axios from 'axios'
import { ContentType } from '../utils/contentType'

interface ActionsWithPublicIds {
  topicId: number | null
  postId: number
  privateId?: undefined
  privateReplyId?: undefined
  contentType?: undefined
  onEditClick?: undefined
}

interface ActionsWithReplyId {
  topicId?: undefined
  postId: number
  privateId?: undefined
  privateReplyId: number
  contentType: ContentType
  onEditClick: () => void
}

interface ActionsWithTopicId {
  topicId: number
  postId?: undefined
  privateId: number
  privateReplyId?: undefined
  contentType: ContentType
  onEditClick: () => void
}

interface NoLiking {
  isLiked?: undefined
  showLike: false
}

interface WithLiking {
  isLiked: boolean
  showLike: true
}

type Props = {
  onLikeClick?: () => void
  showEdit: boolean
  showReport: boolean
  showBestAnswer: boolean
  quoteText: string
  quoteUsername: string
} & (ActionsWithPublicIds | ActionsWithReplyId | ActionsWithTopicId) & (NoLiking | WithLiking)

interface State {
  isExtraMenuOpen: boolean
  isShareMenuOpen: boolean
  isLiked: boolean|undefined
  isBestAnswerClicked: boolean
}

class PostActions extends Component<Props, State> {
  likePending?: boolean
  shareTimeout?: NodeJS.Timeout

  constructor (props: Props) {
    super(props)
    this.state = {
      isExtraMenuOpen: false,
      isShareMenuOpen: false,
      isLiked: props.isLiked,
      isBestAnswerClicked: false
    }
    this.onLikeClick = this.onLikeClick.bind(this)
    this.onQuoteClick = this.onQuoteClick.bind(this)
    this.onEditClick = this.onEditClick.bind(this)
    this.onShareEnter = this.onShareEnter.bind(this)
    this.onShareLeave = this.onShareLeave.bind(this)
    this.handleBestAnswerClick = this.handleBestAnswerClick.bind(this)
  }

  showLoginModal (e: Event) {
    e.preventDefault()
    window.dispatchEvent(new CustomEvent('GuestUserAction'))
  }

  updateLikeState (isLiked: boolean) {
    this.setState({ isLiked: isLiked })

    if (this.props.onLikeClick) {
      this.props.onLikeClick()
      return
    }
    // trigger legacy user like list to update
    window.app.events.commands.execute(`post:like:${this.props.postId}`, {
      currentUserHasLiked: isLiked
    })
  }

  async makeLikeRequest (newLikeState: boolean) {
    if (this.props.privateReplyId && this.props.contentType) {
      return await axios.post<{likedBy: number}>(`/community/api/${this.props.contentType}s/replies/${this.props.privateReplyId}/${newLikeState ? 'like' : 'unlike'}`, { likedBy: window.inSidedData.user.userid })
    }

    if (this.props.postId) {
      return await axios.post<{liked: boolean, postid: number}>(`/member/like?like=${newLikeState}${this.props.topicId ? `&tid=${this.props.topicId}` : ''}`, {
        liked: newLikeState,
        postid: this.props.postId
      })
    }
    return await axios.post<{likedBy: number}>(`/community/api/${this.props.contentType}s/${this.props.privateId}/${newLikeState ? 'like' : 'unlike'}`, { likedBy: window.inSidedData.user.userid })
  }

  async onLikeClick (e: Event) {
    e.preventDefault()
    if (this.likePending) return
    this.likePending = true
    const newIsLiked = !this.state.isLiked
    this.updateLikeState(newIsLiked)

    try {
      await this.makeLikeRequest(newIsLiked)
    } catch (e) {
      this.updateLikeState(!newIsLiked)
      throw e
    } finally {
      this.likePending = false
    }
  }

  onQuoteClick (e: Event) {
    e.preventDefault()
    publish('quote', {
      username: this.props.quoteUsername,
      content: this.props.quoteText
    })
  }

  onEditClick (e: Event) {
    if (!this.props.onEditClick) return
    e.preventDefault()
    this.props.onEditClick()
  }

  onShareEnter () {
    if (this.shareTimeout) clearTimeout(this.shareTimeout)
    this.setState({ isShareMenuOpen: true })
  }

  onShareLeave () {
    this.shareTimeout = setTimeout(() => this.setState({ isShareMenuOpen: false }), 300)
  }

  handleBestAnswerClick (e: Event) {
    if (this.state.isBestAnswerClicked) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    this.setState({
      isBestAnswerClicked: true
    })
  }

  render (props: Props, state: State) {
    return (
      <ModulesContext.Consumer>
        {modules => (
          <CurrentUserContext.Consumer>
            {user => (
              <ul class='list clearfix text--meta post__actions qa-post-actions'>
                {props.showBestAnswer
                  ? <li class='post__action-item' data-helper='mark-answer'>
                    <a href={`/topic/markanswer?postid=${props.postId}`} onClick={this.handleBestAnswerClick} class='icon icon--b icon--checkmark link--quiet post__action-link js-mark-answer'>
                      <span class='post__action-link__text'>
                        <Phrase p='Best answer' />
                      </span>
                    </a>
                  </li>
                  : null}

                {props.showLike
                  ? <li class='post__action-item'>
                    <a class={`link link--quiet icon--b icon--thumb-up qa-like-link post__action-link ${state.isLiked ? 'is-active' : ''}`} href='#' onClick={user.id ? this.onLikeClick : this.showLoginModal}>
                      <span class='post__action-link__text'>
                        <Phrase m='Common' key={state.isLiked ? 'unlike' : 'like'} p={state.isLiked ? 'unlike' : 'like'} />
                      </span>
                    </a>
                  </li>
                  : null}

                <li class='post__action-item'>
                  <a class='link link--quiet icon--b icon--quote qa-icon-quote post__action-link' href='#' onClick={this.onQuoteClick}>
                    <span class='post__action-link__text'>
                      <Phrase p='Quote' />
                    </span>
                  </a>
                </li>

                {props.topicId
                  ? <li class='post__action-item'>
                    <TopicFavorite id={props.topicId} />
                  </li>
                  : null}

                {props.topicId && modules.includes('TopicSocialShare')
                  ? <li class='post__action-item'>
                    <div class='tooltip--share'>
                      <div class='tooltip tooltip--day' onMouseLeave={this.onShareLeave} onMouseEnter={this.onShareEnter}>

                        <span class='link link--quiet icon--b icon--share tooltip-trigger post__action-link'>
                          <span class='post__action-link__text'>
                            <Phrase p='Share' />
                          </span>
                        </span>
                        {state.isShareMenuOpen
                          ? <div class='tooltip__content js-tooltip-content position--bottom is-enabled is-active'>
                            <i class='arrow' style='left: 50%' />

                            <Phrase p='twitter.share.title'>
                              {twitterPhrase => (
                                <a title={twitterPhrase} class='btn btn--icon btn--twitter qa-button-twitter' href={`http://twitter.com/intent/tweet?text=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='twitter.share' />
                                </a>
                              )}</Phrase>

                            <Phrase p='facebook.share.title'>
                              {facebookPhrase => (
                                <a title={facebookPhrase} class='btn btn--icon btn--facebook qa-button-facebook' href={`https://www.facebook.com/sharer/sharer.php?u=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='facebook.share' />
                                </a>
                              )}</Phrase>

                            <Phrase p='linkedin.share.title'>
                              {linkedinPhrase => (
                                <a title={linkedinPhrase} class='btn btn--icon btn--linkedin qa-button-linkedin' href={`https://www.linkedin.com/shareArticle?mini=true&url=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='linkedin.share' />
                                </a>
                              )}</Phrase>

                            <Phrase p='whatsapp.share.title'>
                              {whatsappPhrase => (
                                <a title={whatsappPhrase} class='btn btn--icon btn--whatsapp icon icon--whatsapp is-hidden-mobile-up' href={`http://www.linkedin.com/shareArticle?mini=true&amp;url=https://${window.location.hostname + window.location.pathname}`} target='_blank' rel='noreferrer noopener'>
                                  <Phrase p='whatsapp.share' />
                                </a>
                              )}</Phrase>

                          </div>
                          : null}
                      </div>
                    </div>
                  </li>
                  : null}

                {user.id
                  ? <ExtraMenu privateReplyId={props.privateReplyId} postId={props.postId} showEdit={props.showEdit} showReport={props.showReport} onEditClick={this.onEditClick} />
                  : null}
              </ul>
            )}
          </CurrentUserContext.Consumer>
        )}
      </ModulesContext.Consumer>
    )
  }
}

interface ExtraMenuState {
  isExtraMenuOpen: boolean
}

interface ExtraMenuProps {
  postId?: number
  privateReplyId?: number
  showEdit: boolean
  showReport: boolean
  onEditClick: (e: Event) => void
}

class ExtraMenu extends Component<ExtraMenuProps, ExtraMenuState> {
  extraTimeout?: NodeJS.Timeout

  constructor () {
    super()
    this.onExtraEnter = this.onExtraEnter.bind(this)
    this.onExtraLeave = this.onExtraLeave.bind(this)
    this.onReportClick = this.onReportClick.bind(this)
  }

  onExtraEnter () {
    if (this.extraTimeout) clearTimeout(this.extraTimeout)
    this.setState({ isExtraMenuOpen: true })
  }

  onExtraLeave () {
    this.extraTimeout = setTimeout(() => this.setState({ isExtraMenuOpen: false }), 300)
  }

  onReportClick (e: Event) {
    e.preventDefault()
    // trigger legacy report modal
    window.app.events.commands.execute('modal:report:content', `/post/report?postid=${this.props.postId}`)
  }

  getPermalink () {
    const topicLink = `https://${window.location.hostname}${window.location.pathname}`
    if (!this.props.postId) return topicLink
    const id = this.props.privateReplyId || this.props.postId
    return `${topicLink}?postid=${id}#post${id}`
  }

  render (props: ExtraMenuProps, state: ExtraMenuState) {
    return (
      <li class='tooltip tooltip--day post__action-item post__action-item--more' onMouseEnter={this.onExtraEnter} onMouseLeave={this.onExtraLeave}>
        <a class='post__action-link tooltip-trigger js-tooltip-trigger'>
          <span class='icon--auto-width icon--ellipsis qa-topic-post-ellipsis' />
        </a>
        {state.isExtraMenuOpen
          ? <ul class='list tooltip__content tooltip__content--more position--bottom-right is-enabled is-active'>
            <i class='arrow' />
            {props.showEdit
              ? <li class='more__item post__action-dropdown-item'>
                <a class='link js-edit qa-topic-post-edit post__action-dropdown-link' href={`/post/edit?postid=${props.postId}`} data-postid={props.postId} onClick={this.props.onEditClick}>
                  <Phrase p='Edit' />
                </a>
              </li>
              : null}

            <li class='more__item post__action-dropdown-item'>
              <a class='link qa-topic-post-permalink post__action-dropdown-link' href={this.getPermalink()}>
                <Phrase p='Permalink' />
              </a>
            </li>

            {props.showReport
              ? <li class='more__item post__action-dropdown-item'>
                <a class='link qa-topic-post-report post__action-dropdown-link' onClick={this.onReportClick}>
                  <Phrase p='Flag' />
                </a>
              </li>
              : null}

          </ul>
          : null}
      </li>
    )
  }
}

export default PostActions
